import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";

// Hook for getting ID from path parameters like /workspace/tags/123
export function usePathParam(resourceType: "chat" | "agents" | "prompt-sets" | "tags" | "artifacts" | "documents" | "subspaces" | "users" | "events"): [number | undefined, (id: number | undefined) => void] {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    // Get ID from URL params (dynamic route [id])
    const getId = useCallback(() => {
        if (params.id && typeof params.id === "string") {
            const id = parseInt(params.id);
            return isNaN(id) ? undefined : id;
        }
        return undefined;
    }, [params.id]);

    const [currentId, setCurrentId] = useState<number | undefined>(getId());

    // Update state when URL changes
    useEffect(() => {
        setCurrentId(getId());
    }, [getId]);

    // Function to navigate to a specific ID or back to list
    const setId = useCallback(
        (id: number | undefined) => {
            console.log("Setting ID for", resourceType, "to", id);
            if (id) {
                navigate(`/workspace/${resourceType}/${id}`);
            } else {
                navigate(`/workspace/${resourceType}`);
            }
        },
        [navigate, resourceType],
    );

    return [currentId, setId];
}

// For number lists - this could be used for selected items, filters, etc.
// Since we're moving away from search params, this might be used for local state
// or could be stored in localStorage/sessionStorage if persistence is needed
export function useLocalNumberList(key: string): [number[], (valOrUpdater: number[] | ((prev: number[]) => number[])) => void] {
    const [values, setValues] = useState<number[]>(() => {
        // Initialize from localStorage if available
        if (typeof window !== "undefined") {
            const stored = localStorage.getItem(key);
            return stored ? JSON.parse(stored) : [];
        }
        return [];
    });

    const setStoredValues = useCallback(
        (valOrUpdater: number[] | ((prev: number[]) => number[])) => {
            const newValues = typeof valOrUpdater === "function" ? valOrUpdater([...values]) : valOrUpdater;

            setValues(newValues);

            // Persist to localStorage
            if (typeof window !== "undefined") {
                if (newValues.length > 0) {
                    localStorage.setItem(key, JSON.stringify(newValues));
                } else {
                    localStorage.removeItem(key);
                }
            }
        },
        [values, key],
    );

    return [values, setStoredValues];
}
